import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function Landing() {
  const [activeTab, setActiveTab] = useState<'parents' | 'schools'>('parents');

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-primary-600">IndieSchools</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-gray-600 hover:text-gray-900">
                Sign In
              </Link>
              <Link to="/register" className="btn btn-primary">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 via-white to-primary-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Find the Perfect School<br />for Your Child
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Discover and connect with independent schools across the UK. Search by location,
              curriculum, and specialisms to find the ideal fit for your family.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register?role=parent" className="btn btn-primary btn-lg">
                Search Schools
              </Link>
              <Link to="/register?role=school" className="btn btn-secondary btn-lg">
                List Your School
              </Link>
            </div>
            <p className="text-sm text-gray-500 mt-6">
              Join 200+ families and 50+ schools already using IndieSchools
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">Simple steps to find your ideal school</p>
          </div>

          {/* Tab Selection */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex rounded-lg border border-gray-200 p-1">
              <button
                onClick={() => setActiveTab('parents')}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${
                  activeTab === 'parents'
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                For Parents
              </button>
              <button
                onClick={() => setActiveTab('schools')}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${
                  activeTab === 'schools'
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                For Schools
              </button>
            </div>
          </div>

          {/* Parent Steps */}
          {activeTab === 'parents' && (
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🔍</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">1. Search & Filter</h3>
                <p className="text-gray-600">
                  Browse schools by location, curriculum, fees, and age range. Save your favorites to a shortlist.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">📧</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">2. Send Enquiries</h3>
                <p className="text-gray-600">
                  Contact schools directly with personalized enquiries. Track responses all in one place.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🎓</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">3. Receive Offers</h3>
                <p className="text-gray-600">
                  Review offers from schools including fees, scholarships, and available year groups.
                </p>
              </div>
            </div>
          )}

          {/* School Steps */}
          {activeTab === 'schools' && (
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🏫</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">1. Create Profile</h3>
                <p className="text-gray-600">
                  Showcase your school with detailed information, photos, and unique specialisms.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">📨</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">2. Manage Enquiries</h3>
                <p className="text-gray-600">
                  Receive and respond to parent enquiries. Track all communications in your dashboard.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">✉️</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">3. Send Offers</h3>
                <p className="text-gray-600">
                  Make placement offers with customized fees and scholarship information.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Everything You Need</h2>
            <p className="text-xl text-gray-600">Powerful features to streamline school discovery</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-3xl mb-3">🗺️</div>
              <h3 className="text-lg font-semibold mb-2">Location-Based Search</h3>
              <p className="text-gray-600 text-sm">
                Find schools within your preferred distance. Smart geolocation filtering.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-3xl mb-3">🎯</div>
              <h3 className="text-lg font-semibold mb-2">Advanced Filters</h3>
              <p className="text-gray-600 text-sm">
                Filter by curriculum, fees, age range, school type, and specialisms.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-3xl mb-3">⭐</div>
              <h3 className="text-lg font-semibold mb-2">Shortlist Management</h3>
              <p className="text-gray-600 text-sm">
                Save and compare your favorite schools. Keep everything organized.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-3xl mb-3">💬</div>
              <h3 className="text-lg font-semibold mb-2">Direct Messaging</h3>
              <p className="text-gray-600 text-sm">
                Communicate securely with schools. Track all conversations in one place.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-3xl mb-3">📊</div>
              <h3 className="text-lg font-semibold mb-2">Application Tracking</h3>
              <p className="text-gray-600 text-sm">
                Monitor enquiry status, offers, and deadlines at a glance.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-3xl mb-3">🔔</div>
              <h3 className="text-lg font-semibold mb-2">Real-Time Notifications</h3>
              <p className="text-gray-600 text-sm">
                Get instant updates when schools respond or send offers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* School Showcase */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Featured Schools</h2>
            <p className="text-xl text-gray-600">Discover exceptional independent schools</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <div className="card hover:shadow-lg transition-shadow">
              <div className="h-48 bg-gradient-to-br from-primary-100 to-primary-200 rounded-t-lg mb-4"></div>
              <h3 className="text-xl font-semibold mb-2">St. Albans Academy</h3>
              <p className="text-sm text-gray-600 mb-2">London • British Curriculum</p>
              <p className="text-sm text-gray-700 mb-4">
                Academic excellence with strong arts and languages programme.
              </p>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-primary-600">£12,000/year</span>
                <span className="badge badge-success">Available</span>
              </div>
            </div>

            <div className="card hover:shadow-lg transition-shadow">
              <div className="h-48 bg-gradient-to-br from-blue-100 to-blue-200 rounded-t-lg mb-4"></div>
              <h3 className="text-xl font-semibold mb-2">Riverside College</h3>
              <p className="text-sm text-gray-600 mb-2">Bath • IB Programme</p>
              <p className="text-sm text-gray-700 mb-4">
                Small cohorts and high academic outcomes with IB specialism.
              </p>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-primary-600">£14,000/year</span>
                <span className="badge badge-success">Available</span>
              </div>
            </div>

            <div className="card hover:shadow-lg transition-shadow">
              <div className="h-48 bg-gradient-to-br from-green-100 to-green-200 rounded-t-lg mb-4"></div>
              <h3 className="text-xl font-semibold mb-2">Greenwood Prep</h3>
              <p className="text-sm text-gray-600 mb-2">Surrey • British Curriculum</p>
              <p className="text-sm text-gray-700 mb-4">
                Community-centered prep with modern facilities and sports.
              </p>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-primary-600">£9,500/year</span>
                <span className="badge badge-warning">Limited</span>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <Link to="/register" className="btn btn-primary btn-lg">
              Browse All Schools
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-4">
              Ready to Find Your Perfect School?
            </h2>
            <p className="text-xl mb-8 text-primary-100">
              Join hundreds of families who have simplified their school search with IndieSchools.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register?role=parent" className="btn bg-white text-primary-600 hover:bg-gray-100 btn-lg">
                Get Started Free
              </Link>
              <Link to="/login" className="btn border-2 border-white text-white hover:bg-primary-700 btn-lg">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h4 className="text-white font-semibold mb-4">IndieSchools</h4>
              <p className="text-sm">
                Connecting independent schools with families across the UK.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">For Parents</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/register" className="hover:text-white">Search Schools</Link></li>
                <li><Link to="/register" className="hover:text-white">Create Account</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">For Schools</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/register?role=school" className="hover:text-white">List Your School</Link></li>
                <li><Link to="/login" className="hover:text-white">School Login</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">Help Center</a></li>
                <li><a href="#" className="hover:text-white">Contact Us</a></li>
                <li><Link to="/login" className="hover:text-white">Admin Access</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm">
            <p>&copy; 2024 IndieSchools. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
